import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'
import { LoadingState } from '@platform/ui-kit'
import { AppShell } from '@/app/shell/AppShell'
import { ModuleGuard, RequireAuth, RequireModule, RequirePermission } from './guards'
import { MODULES } from './moduleRegistry'
import { ErrorBoundary } from '@/app/errors/ErrorBoundary'
import { useDocumentTitle } from '@/app/shell/useDocumentTitle'

/* ------------------------------------------------------------------
   مسیرها — سند مادر بند ۱۱.۱
   ------------------------------------------------------------------
   Deep Link پایدار:  /app/<module>/<resource>/<id>

   تمام صفحات به‌صورت Lazy بارگذاری می‌شوند تا هر ماژول Bundle
   جداگانه داشته باشد (بند ۵.۲).
   ------------------------------------------------------------------ */

const LoginPage = lazy(() => import('@/features/auth/LoginPage'))
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'))
const InboxPage = lazy(() => import('@/features/inbox/InboxPage'))
const ChatPage = lazy(() => import('@/features/chat/ChatPage'))
const ChatAppPage = lazy(() => import('@/features/chat/ChatAppPage'))
const NotificationCenterPage = lazy(
  () => import('@/features/notifications/NotificationCenterPage'),
)
const ChangePasswordPage = lazy(() => import('@/features/auth/ChangePasswordPage'))
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage'))

const NotificationPolicyPage = lazy(
  () => import('@/modules/notifications/pages/NotificationPolicyPage'),
)

/* ------------------------------------------------------------------
   مسیرِ بی‌نشانیِ ریشه — فقط برای عنوان تب
   ------------------------------------------------------------------
   بالای همهٔ مسیرها می‌نشیند تا ورود و داشبورد و ماژول‌ها عنوانشان را
   از یک جا بگیرند. داخل `RouterProvider` است، پس `useLocation` را
   دارد — چیزی که `main.tsx` بیرون از Router ندارد.
   ------------------------------------------------------------------ */
function TitledRoutes() {
  useDocumentTitle()
  return <Outlet />
}

export const router = createBrowserRouter([
  {
    element: <TitledRoutes />,
    children: [
      {
        path: '/login',
        element: <LoginPage />,
      },

      /* «Yekpay Chat» — برنامهٔ نصبی، بی نوار و منوی پنل (`features/chat/chatApp.ts`). */
      {
        path: '/chat-app/login',
        element: <LoginPage />,
      },
      {
        path: '/chat-app',
        element: (
          <RequireAuth loginPath="/chat-app/login">
            <Suspense fallback={<LoadingState label="در حال اتصال به گفتگو…" />}>
              <ChatAppPage />
            </Suspense>
          </RequireAuth>
        ),
        errorElement: <ErrorBoundary>{null}</ErrorBoundary>,
      },

      {
        path: '/app',
        element: (
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        ),
        // خطای بارگذاری Route نباید صفحه سفید بدهد
        errorElement: <ErrorBoundary>{null}</ErrorBoundary>,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'inbox', element: <InboxPage /> },

          /* گفتگو — Rocket.Chat در قابِ پنل، با ورودِ خودکار. */
          { path: 'chat', element: <ChatPage /> },

          /*
            🔴 زنگِ بالای صفحه از روز اول به اینجا می‌رفت — و این مسیر
            وجود نداشت. کلیک روی زنگ هیچ کاری نمی‌کرد.
          */
          { path: 'notifications', element: <NotificationCenterPage /> },

          /* مسیر خروج از must_change_password — RequireAuth کاربر را تا
             تغییر رمز به همین‌جا برمی‌گرداند، پس باید همیشه وجود داشته باشد. */
          { path: 'profile/password', element: <ChangePasswordPage /> },

          /*
            تنظیم‌های شخصی کاربر — خارج از ماژول‌ها.

            ⚠️ عمداً بدون `RequireModule`: خودِ صفحه هر بخش را بر
            اساس دسترسی کاربر فیلتر می‌کند، چون بخش‌هایش به
            ماژول‌های مختلفی تعلق دارند و یک دروازهٔ واحد نمی‌تواند
            دربارهٔ همه‌شان تصمیم بگیرد.
          */
          { path: 'settings', element: <SettingsPage /> },

          /*
            تنظیم اعلان‌ها — تصمیمِ ادمین برای همه.

            🔴 چرا صفحهٔ جدا و نه بخشی در `/app/settings`: آن صفحه
            «تنظیم‌های شخصی شما» است و این تصمیمی است که روی همهٔ
            کاربران اثر می‌گذارد. کنارِ هم گذاشتنشان یعنی ادمین
            نداند کدام کلید فقط خودش را عوض می‌کند و کدام کلید کلِ
            سازمان را.

            ⚠️ `RequirePermission` فقط زودتر می‌گوید؛ خودِ Endpoint
            هم مستقل بررسی می‌کند (AC-02).
          */
          {
            path: 'settings/notification-policy',
            element: (
              <RequirePermission permission="platform.setting.view">
                <NotificationPolicyPage />
              </RequirePermission>
            ),
          },

          /* هر ماژول زیردرخت مسیر خودش را دارد.
             `*` باعث می‌شود مسیرهای داخلی ماژول (لیست، جزئیات، فرم)
             توسط خود ماژول مدیریت شوند و Shell دخالتی نکند. */
          /*
           | 🔴 دو دروازه، دو پرسش متفاوت.
           |
           | `RequireModule` می‌پرسد «آیا این ماژول اصلاً برای این کاربر
           | وجود دارد؟» و `ModuleGuard` می‌پرسد «آیا این **بخشِ** ماژول
           | برای او مجاز است؟»
           |
           | ⚠️ دومی تا امروز فقط در ۴ ماژول و دستی نوشته شده بود؛ ۶ ماژول
           | دیگر مجوزِ بخش‌هایشان را در رجیستری اعلام می‌کردند ولی هیچ‌جا
           | اعمال نمی‌شد. حالا مرکزی است و از قلم نمی‌افتد.
           */
          ...MODULES.map((module) => ({
            path: `${module.path}/*`,
            element: (
              <RequireModule moduleKey={module.key}>
                <ModuleGuard moduleKey={module.key}>
                  <module.component />
                </ModuleGuard>
              </RequireModule>
            ),
          })),

          { path: '*', element: <Navigate to="/app" replace /> },
        ],
      },

      { path: '/', element: <Navigate to="/app" replace /> },
      { path: '*', element: <Navigate to="/app" replace /> },
    ],
  },
])
